import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize, Pause, Play, Settings as SettingsIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { HouseAdSlide } from './components/HouseAdSlide';
import { Layout } from './components/Layout';
import { PrivacyPolicy, TermsOfService } from './components/LegalPage';
import { MediaStage } from './components/MediaStage';
import { PairPage } from './components/PairPage';
import { startCheckout } from './components/settings/AccountTab';
import { SettingsPanel } from './components/settings/SettingsPanel';
import { useAdManager } from './hooks/useAdManager';
import { toggleFullscreen, useActivity, useWakeLock } from './hooks/useDisplay';
import { useMediaQueue } from './hooks/useMediaQueue';
import { useSettingsSync } from './hooks/useSettingsSync';
import { sanitizeCss } from './lib/sanitizeCss';
import { endpoints } from './services/api';
import { isLicensed, useSession } from './store/session';
import { restoreLocalMedia, useSettings } from './store/settings';

export function App() {
  const path = window.location.pathname.replace(/\/+$/, '');
  if (path === '/privacy') return <PrivacyPolicy />;
  if (path === '/terms') return <TermsOfService />;
  if (path === '/pair') return <PairPage />;
  return <Display />;
}

const AUTH_ERRORS: Record<string, string> = {
  cancelled: 'Microsoft sign-in was cancelled.',
  email_exists:
    'An account with that email already exists. Sign in with your password, then connect Microsoft from Settings → Account.',
  linked_elsewhere: 'That Microsoft account is already connected to a different KCL Canvas account.',
  sign_in_first: 'Sign in first, then connect your Microsoft account.',
  session_changed: 'You signed out while connecting. Please sign in and try again.',
  not_configured: 'Microsoft sign-in isn’t switched on yet.',
};

function useThemeAndCss(): void {
  const theme = useSettings((s) => s.theme);
  const customCss = useSettings((s) => s.customCss);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);
  const safe = useMemo(() => sanitizeCss(customCss), [customCss]);
  useEffect(() => {
    let el = document.getElementById('kcl-custom-css') as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = 'kcl-custom-css';
      document.head.appendChild(el);
    }
    el.textContent = safe; // textContent, never innerHTML
  }, [safe]);
}

function Display() {
  useThemeAndCss();
  useWakeLock();
  useSettingsSync();

  const { load, loaded, user, config, setUser } = useSession();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<string | undefined>(undefined);
  const [paused, setPaused] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const active = useActivity();

  useEffect(() => {
    void load();
    void restoreLocalMedia();
  }, [load]);

  // Returning from Stripe Checkout or a Microsoft sign-in/connect redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    const authError = params.get('auth_error');
    const signedIn = params.get('signed_in');
    const connected = params.get('connected');
    if (!checkout && !authError && !signedIn && !connected) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (authError) {
      setToast(AUTH_ERRORS[authError] ?? 'Microsoft sign-in didn’t complete. Please try again.');
      return;
    }
    if (signedIn || connected) {
      setToast(signedIn ? 'Signed in with Microsoft.' : 'Microsoft account connected – choose OneDrive folders in Settings → Media.');
      return; // load() above already picked up the new session
    }
    if (checkout === 'success') {
      setToast('Thank you! Your lifetime licence is active – no more ads.');
      // The webhook can land a moment after the redirect.
      const refresh = () => endpoints.me().then((r) => r.user && setUser(r.user)).catch(() => undefined);
      void refresh();
      window.setTimeout(refresh, 4000);
    } else {
      setToast('Checkout cancelled – nothing was charged.');
    }
  }, [setUser]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const adsEnabled = loaded && !isLicensed(user);
  const { ad, dismiss } = useAdManager(adsEnabled);
  const queue = useMediaQueue(paused || settingsOpen || ad !== null);

  const openSettings = useCallback((tab?: string) => {
    setSettingsTab(tab);
    setSettingsOpen(true);
  }, []);

  const removeAds = useCallback(async () => {
    dismiss();
    if (config?.features.billing && user) {
      const error = await startCheckout();
      if (error) setToast(error);
    } else {
      openSettings('account');
    }
  }, [config, user, dismiss, openSettings]);

  // Keyboard / TV remote shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (settingsOpen || e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'MediaTrackNext':
          queue.next();
          break;
        case 'ArrowLeft':
        case 'MediaTrackPrevious':
          queue.prev();
          break;
        case ' ':
        case 'MediaPlayPause':
          e.preventDefault();
          setPaused((p) => !p);
          break;
        case 'f':
        case 'F':
          toggleFullscreen();
          break;
        case 's':
        case 'S':
        case 'm':
        case 'M':
        case 'ContextMenu':
          openSettings();
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [settingsOpen, queue, openSettings]);

  // Command Center stacks widgets top-right, so the controls move left there.
  const controlsSide = useSettings((s) => (s.layout === 'command' ? 'left-4' : 'right-4'));
  const hasMedia = useSettings((s) => s.media.items.length > 0 || s.media.cloudFolders.length > 0);
  const hasLocation = useSettings((s) => s.weather.location !== null);
  const firstRun = !hasMedia && !hasLocation;

  return (
    <main className={`relative h-full w-full overflow-hidden ${active || settingsOpen ? '' : 'hide-cursor'}`}>
      <MediaStage
        entry={queue.current}
        cycle={queue.cycle}
        intervalSec={queue.intervalSec}
        onEnded={queue.next}
        onError={queue.markFailed}
        loopVideo={queue.total <= 1}
      />
      <Layout />

      <AnimatePresence>{ad && <HouseAdSlide key={ad.id} ad={ad} onClose={dismiss} onRemoveAds={() => void removeAds()} />}</AnimatePresence>

      {/* Controls fade in on activity. */}
      <AnimatePresence>
        {(active || paused) && !settingsOpen && (
          <motion.div
            className={`glass absolute ${controlsSide} top-4 z-20 flex items-center gap-1 p-1.5`}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            {queue.total > 1 && (
              <>
                <IconButton label="Previous" onClick={queue.prev} icon={<ChevronLeft className="h-5 w-5" />} />
                <IconButton
                  label={paused ? 'Play' : 'Pause'}
                  onClick={() => setPaused((p) => !p)}
                  icon={paused ? <Play className="h-5 w-5" /> : <Pause className="h-5 w-5" />}
                />
                <IconButton label="Next" onClick={queue.next} icon={<ChevronRight className="h-5 w-5" />} />
              </>
            )}
            <IconButton label="Full screen (F)" onClick={toggleFullscreen} icon={<Maximize className="h-5 w-5" />} />
            <IconButton label="Settings (S)" onClick={() => openSettings()} icon={<SettingsIcon className="h-5 w-5" />} />
          </motion.div>
        )}
      </AnimatePresence>

      {firstRun && !settingsOpen && (
        <button
          onClick={() => openSettings('media')}
          className="glass absolute left-1/2 top-8 z-20 -translate-x-1/2 px-5 py-3 text-sm"
        >
          Welcome to KCL Canvas – press <kbd className="rounded border border-[var(--border)] px-1.5">S</kbd> or tap here to add
          your photos, weather and calendar.
        </button>
      )}

      <AnimatePresence>
        {toast && (
          <motion.div
            className="glass absolute bottom-24 left-1/2 z-50 -translate-x-1/2 px-5 py-3 text-sm"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            role="status"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <SettingsPanel open={settingsOpen} onClose={() => setSettingsOpen(false)} initialTab={settingsTab} />
    </main>
  );
}

function IconButton({ label, onClick, icon }: { label: string; onClick: () => void; icon: ReactNode }) {
  return (
    <button onClick={onClick} aria-label={label} title={label} className="rounded-xl p-2.5 transition hover:bg-[var(--border)]">
      {icon}
    </button>
  );
}
